import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import ImageUpload from '../../components/ImageUpload';
import { useAuth } from '../../lib/auth';
import API from '../../lib/api';
import { toast } from 'sonner';

const inputCls = 'bg-[#232323] border-white/[0.10] rounded-xl h-11 text-sm text-white placeholder:text-white/25 focus:border-gold focus:ring-1 focus:ring-gold/20';
const labelCls = 'text-[10px] uppercase tracking-widest text-white/35 mb-1.5 block font-medium';

export default function AdminLocationForm() {
  const { id } = useParams();
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    name: '', slug: '', short_description: '', full_description: '',
    hero_image: '', thumbnail_image: '', seo_title: '', seo_description: '',
    is_featured: false, is_visible: true, display_order: 0
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate('/login'); return; }
    if (isEdit) { API.get(`/admin/locations/${id}`).then(r => { setForm(r.data); setLoading(false); }).catch(() => { toast.error('Location not found'); navigate('/admin/locations'); }); }
  }, [id, isAdmin, navigate, isEdit, authLoading]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) { await API.put(`/admin/locations/${id}`, form); toast.success('Location updated'); }
      else { await API.post('/admin/locations', form); toast.success('Location created'); }
      navigate('/admin/locations');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-location-form">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/admin/locations" className="inline-flex items-center gap-1.5 text-white/30 hover:text-gold text-xs uppercase tracking-widest mb-3 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Back to Locations</Link>
          <h1 className="font-heading text-2xl text-white font-bold">{isEdit ? 'Edit Location' : 'New Location'}</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Basic Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Location Name *</label><Input value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Toronto" className={inputCls} data-testid="location-name" /></div>
              <div><label className={labelCls}>Slug (auto-generated if empty)</label><Input value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="e.g. toronto" className={inputCls} data-testid="location-slug" /></div>
            </div>
            <div><label className={labelCls}>Short Description</label><Input value={form.short_description} onChange={e => set('short_description', e.target.value)} placeholder="Brief tagline" className={inputCls} data-testid="location-short-desc" /></div>
            <div><label className={labelCls}>Full Description</label>
              <textarea value={form.full_description} onChange={e => set('full_description', e.target.value)} rows={5} placeholder="Detailed description for the location page"
                className="w-full bg-[#232323] border border-white/[0.10] rounded-xl p-4 text-sm text-white resize-none focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 placeholder:text-white/25" data-testid="location-full-desc" /></div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Images</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <ImageUpload value={form.hero_image} onChange={v => set('hero_image', v)} label="Hero Image (Location Page Banner)" />
              <ImageUpload value={form.thumbnail_image} onChange={v => set('thumbnail_image', v)} label="Thumbnail Image (Cards & Grid)" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">SEO</h2>
            <div><label className={labelCls}>SEO Title</label><Input value={form.seo_title} onChange={e => set('seo_title', e.target.value)} placeholder="Custom page title" className={inputCls} /></div>
            <div><label className={labelCls}>SEO Description</label><Input value={form.seo_description} onChange={e => set('seo_description', e.target.value)} placeholder="Meta description" className={inputCls} /></div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
            <h2 className="font-heading text-base font-semibold text-white mb-5">Settings</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center justify-between"><label className="text-sm text-white/50">Featured</label><Switch checked={form.is_featured} onCheckedChange={v => set('is_featured', v)} data-testid="location-featured" /></div>
              <div className="flex items-center justify-between"><label className="text-sm text-white/50">Visible</label><Switch checked={form.is_visible} onCheckedChange={v => set('is_visible', v)} data-testid="location-visible" /></div>
              <div><label className={labelCls}>Display Order</label><Input type="number" value={form.display_order} onChange={e => set('display_order', parseInt(e.target.value) || 0)} className={`${inputCls} w-24`} /></div>
            </div>
          </motion.div>

          <div className="flex items-center gap-4">
            <Button type="submit" disabled={saving} className="bg-gold hover:bg-[#9A7B1F] text-white h-12 px-8 text-xs uppercase tracking-[0.2em] font-semibold rounded-full gap-2" data-testid="save-location">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isEdit ? 'Update Location' : 'Create Location'}
            </Button>
            <Link to="/admin/locations"><Button type="button" variant="outline" className="border-white/[0.10] text-white/40 hover:text-white rounded-full h-12 px-6 text-xs uppercase tracking-widest">Cancel</Button></Link>
          </div>
        </form>
      </div>
    </div>
  );
}
